import { useStateUrlParamsArray } from "../../common/hooks/useStateUrlParams.ts";
import { useState } from "react";
import { TemplatePlanRequirement, TemplatesTableRowModel, TemplatesTableViewModel } from "./Templates.types.ts";
import { put } from "../../common/api/apiShared.ts";
import { useFilterList } from "../../common/hooks/useFilterList.ts";
import { useDotLegalQuery, useDotLegalQueryClient, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useTemplates() {
    const queryClient = useDotLegalQueryClient();

    const [searchedStatus, setSearchedStatus] = useStateUrlParamsArray("status", []);
    const [showCreateTemplate, setShowCreateTemplate] = useState(false);
    const [templateToEdit, setTemplateToEdit] = useState<TemplatesTableRowModel | undefined>(undefined);
    const [showToggleIsPublishedDialog, setShowToggleIsPublishedDialog] = useState<TemplatesTableRowModel | undefined>(undefined);

    const url = "/frameworks/templates";
    const { isLoading, data, refetch } = useDotLegalQuery<Array<TemplatesTableRowModel>>(url);

    let filteredTemplates: TemplatesTableViewModel[] = useFilterList(mapToTableData(data ?? []), [{ key: "isPublished", searchValues: searchedStatus }]);

    const updateFrameworkOrderMutation = useDotLegalMutation(updateFrameworkOrderApi);
    const updateFrameworkOrder = (sourceElementId: string, destinationElementId: string) => {
        if (!data) return;

        const sourceElement = data.find((x) => x.id === sourceElementId);
        const destinationElement = data.find((x) => x.id === destinationElementId);

        const tempData = [...data];
        const [removed] = tempData.splice(sourceElement!.order, 1);
        tempData.splice(destinationElement!.order, 0, removed);
        const updatedArray = tempData.map((item, index) => ({
            ...item,
            order: index,
        }));

        queryClient.setQueryData(url, updatedArray);

        updateFrameworkOrderMutation.mutateAsync({ sourceOrder: sourceElement!.order, destinationOrder: destinationElement!.order });
    };

    return {
        isSavingOrder: updateFrameworkOrderMutation.isLoading,
        isLoading,
        data: filteredTemplates,
        refetch,
        searchedStatus,
        setSearchedStatus,
        showCreateTemplate,
        setShowCreateTemplate,
        templateToEdit,
        setTemplateToEdit,
        showToggleIsPublishedDialog,
        setShowToggleIsPublishedDialog,
        updateFrameworkOrder,
    };

    function mapToTableData(data: Array<TemplatesTableRowModel>) {
        return data.map((x) => {
            return {
                ...x,
                complianceAreasString: x.complianceAreas.map((x) => x.name).join(", "),
                templatePlanRequirementsString: formatTemplateRequirementPlansToString(x.templatePlanRequirements),
            };
        });
    }

    function updateFrameworkOrderApi(model: { sourceOrder: number; destinationOrder: number }) {
        return put("/frameworks/templates/updateOrder", model);
    }
}

export function formatTemplateRequirementPlansToString(plans: Array<TemplatePlanRequirement>) {
    return plans.length === 0
        ? ".legal Compliance Platform (Free)"
        : plans
              .map((x) => getTemplateRequirementPlanName(x))
              .sort()
              .join(", ");
}

export function getTemplateRequirementPlanName(plan: TemplatePlanRequirement) {
    switch (plan) {
        case TemplatePlanRequirement.DataProtection:
            return "Data Protection";
        case TemplatePlanRequirement.InformationSecurity:
            return "Information- & Cyber Security";
        case TemplatePlanRequirement.VendorManagement:
            return "Vendor Management";
        case TemplatePlanRequirement.ContractManagement:
            return "Contract Management";
        case TemplatePlanRequirement.Frameworks:
            return "Frameworks";
    }
}
